import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { triggerConfetti } from './confetti';
import {
  buildSteps,
  visibleSections,
  firstMissingAnswer,
  buildAnswers,
  initialAnswers,
  toggleOption,
  plainText,
} from './surveyModel';

/*
  The post-election survey. The questions come from Quickbase through GET /api/survey: one step
  per set the DJ worked, then the general questions, each grouped under its category. Answers go
  back through POST /api/submitSurvey. The rules (follow-ups, required answers, what is sent)
  live in surveyModel.js.
*/
export default function SurveySection({ dj, djElection }) {
  const djElectionRid = djElection?.rid;
  // 'loading' | 'ready' | 'error'
  const [loadState, setLoadState] = useState(djElectionRid ? 'loading' : 'ready');
  const [survey, setSurvey] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    if (!djElectionRid) {
      setSurvey(null);
      setLoadState('ready');
      return undefined;
    }
    let cancelled = false;
    setLoadState('loading');
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/survey`, { params: { dj_election_rid: djElectionRid } })
      .then((response) => {
        if (cancelled) return;
        setSurvey(response.data?.survey || null);
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading survey:', err);
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [djElectionRid]);

  const surveySubmitted = survey?.completed === true;
  const hasSurvey = Boolean(survey) && !surveySubmitted && buildSteps(survey).length > 0;

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = (result = { success: false }) => {
    setIsPopupOpen(false);
    if (result.success) setSurvey((current) => ({ rid: current.rid, completed: true }));
  };

  return (
    <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-6 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">DJs Post-Election Survey</h2>
        {surveySubmitted && (
          <span className="px-3 py-1 text-sm font-medium rounded-lg text-green-800 bg-green-100">
            Completed
          </span>
        )}
      </div>

      <p className="text-gray-700 mb-4">
        Please complete this mandatory survey. If you've checked-in to your sets, we'll send you payment after you've completed the survey.
      </p>

      {loadState === 'loading' ? (
        <p className="text-sm text-gray-600">Loading survey...</p>
      ) : loadState === 'error' ? (
        <div className="p-4 bg-red-50 rounded-lg">
          <p className="text-red-600">We couldn't load your survey. Please refresh the page to try again.</p>
        </div>
      ) : !surveySubmitted ? (
        hasSurvey ? (
          <div>
            <button
              onClick={openPopup}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Fill Survey
            </button>
            <p className="mt-2 text-sm text-gray-600">
              This survey will take approximately 5 minutes to complete.
            </p>
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 rounded-lg">
            <p className="text-yellow-600">No surveys available for you to fill out at this time.</p>
            <p className="text-sm text-gray-600 mt-2">Check back after your election day shift.</p>
          </div>
        )
      ) : (
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-800 font-medium">Survey submitted successfully!</p>
              <p className="text-sm text-green-700 mt-1">Thank you for your feedback. Your payment will be processed soon.</p>
            </div>
          </div>
        </div>
      )}

      {isPopupOpen && hasSurvey && (
        <SurveyPopup onClose={closePopup} dj={dj} djElectionRid={djElectionRid} survey={survey} />
      )}
    </div>
  );
}

const SHIFT_BADGE = {
  Morning: 'bg-amber-100 text-amber-600',
  Afternoon: 'bg-sky-100 text-sky-600',
  Evening: 'bg-purple-100 text-purple-600',
};

function setLabel(set) {
  const label = set.shift || 'Unknown';
  const date = set.shift_date ? moment(set.shift_date, ['YYYY-MM-DD', 'MM-DD-YYYY'], true) : null;
  return date && date.isValid() ? `${date.format('ddd, MM-DD-YYYY')}, ${label} Set` : `${label} Set`;
}

function SurveyPopup({ onClose, djElectionRid, survey }) {
  const [steps] = useState(() => buildSteps(survey));
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(() => initialAnswers(survey));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const contentRef = useRef(null);
  // The element that actually scrolls, so a new step opens at its top.
  const scrollRef = useRef(null);

  const step = steps[stepIndex];
  const setCount = steps.filter((s) => s.kind === 'set').length;
  const isLast = stepIndex === steps.length - 1;
  const sections = visibleSections(step.sections, answers);

  // AUTO-SCROLL FUNCTION
  const scrollToNextSection = (currentSectionId) => {
    if (!contentRef.current) return;
    const sectionEls = contentRef.current.querySelectorAll('.form-section');
    const currentIndex = Array.from(sectionEls).findIndex((section) => section.id === currentSectionId);
    if (currentIndex >= 0 && currentIndex < sectionEls.length - 1) {
      sectionEls[currentIndex + 1].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const setAnswer = (rid, value) => {
    setAnswers((prev) => ({ ...prev, [rid]: value }));
    setError('');
  };

  const validateStep = () => {
    const missing = firstMissingAnswer(step.sections, answers);
    if (missing) {
      setError(`Please answer: ${plainText(missing.html)}`);
      return false;
    }
    return true;
  };

  const goTo = (index) => {
    setStepIndex(index);
    setError('');
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    goTo(stepIndex + 1);
  };

  const handleBack = () => {
    if (stepIndex > 0) goTo(stepIndex - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    const payload = {
      survey_rid: survey.rid,
      dj_election_rid: djElectionRid,
      answers: steps.flatMap((s) => buildAnswers(s.sections, answers)),
    };
    try {
      setSubmitting(true);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/submitSurvey`, payload);
      if (response.data?.success) {
        triggerConfetti();
        onClose({ success: true });
        return;
      }
      setError(response.data?.message || 'Failed to submit survey. Please try again.');
    } catch (err) {
      console.error('Error submitting survey:', err);
      // The popup stays open with the answers, so the DJ can try again.
      setError(err.response?.data?.message || err.message || 'Failed to submit survey. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <>
      {/* Main Modal Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          aria-label="Survey"
          className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto relative flex flex-col max-h-[90vh] max-h-[90dvh]"
        >
          {/* Header: stays put while the questions scroll */}
          <div className="shrink-0 bg-white p-6 border-b border-gray-100 rounded-t-2xl">
            <h2 className="text-2xl font-semibold text-gray-900">Survey</h2>
            {step.kind === 'set' && (
              <div className="mt-6">
                <span
                  className={`px-4 py-2 rounded-md text-2xl font-bold shadow-md w-full block text-center ${
                    SHIFT_BADGE[step.set.shift] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {setLabel(step.set)}
                </span>
                <p className="mt-1 text-lg font-semibold text-gray-600">
                  {step.set.address || 'No address provided'}
                </p>
              </div>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {step.kind === 'set'
                ? `${stepIndex + 1} of ${setCount} set(s) - Please answer the following questions for this set.`
                : 'General Questions - Please answer these questions about your overall experience.'}
            </p>
            <button
              onClick={() => onClose({ success: false })}
              aria-label="Close survey"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content: takes whatever height the header and footer leave */}
          <div ref={scrollRef} className="flex-1 min-h-0 p-6 overflow-y-auto overscroll-contain">
            {sections.map((section) => (
              <div key={`${stepIndex}-${section.category_rid}`}>
                {section.name && <h3 className="text-xl mt-4 font-bold text-primary">{section.name}</h3>}
                {section.questions.map((question) => (
                  <Question
                    key={question.rid}
                    question={question}
                    value={answers[question.rid]}
                    onAnswer={(value, scroll) => {
                      setAnswer(question.rid, value);
                      if (scroll) scrollToNextSection(`q-${question.rid}`);
                    }}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="shrink-0 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mx-4 mb-2">
              <p className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            </div>
          )}

          {/* Footer Navigation: part of the popup, below the questions */}
          <div className="shrink-0 bg-white border-t border-gray-200 p-4 flex justify-between rounded-b-2xl">
            <button
              onClick={handleBack}
              disabled={stepIndex === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                stepIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Back
            </button>
            {!isLast ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm font-medium"
              >
                {steps[stepIndex + 1].kind === 'set' ? 'Next Set' : 'Continue to General Questions'}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  submitting ? 'bg-primary/70 text-white cursor-wait' : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {submitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Survey'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Render the ErrorPopup separately, at a higher z-index */}
      {error && <ErrorPopup errorMessage={error} onClose={() => setError('')} />}
    </>
  );
}

// One question: its rich text, then radios, checkboxes or a text box. The question text comes
// from Quickbase already sanitized by the API (surveyController.js).
function Question({ question, value, onAnswer }) {
  const id = `q-${question.rid}`;
  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`} className="space-y-4 form-section">
      <div id={`${id}-label`} className="block text-lg font-medium text-gray-900">
        {/* Quickbase rich text, rendered the way Next Steps renders it (prose), sized to the old
            question label. Colors, highlights and sizes come through as inline styles. The last
            paragraph stays inline so the required asterisk sits at the end of the text. */}
        <div
          className="prose prose-lg max-w-none inline font-medium text-gray-900 prose-p:text-gray-900 prose-li:text-gray-900 prose-strong:text-gray-900 prose-headings:text-gray-900 prose-a:text-primary prose-p:my-0 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&>p:last-child]:inline"
          dangerouslySetInnerHTML={{ __html: question.html }}
        />
        {question.required && (
          <>
            {' '}
            <span className="text-red-500">*</span>
          </>
        )}
        {question.type === 'multi' && (
          <span className="block text-xs text-gray-500 mt-1">Select all that apply</span>
        )}
      </div>
      {question.type === 'free_text' ? (
        <textarea
          value={value ?? ''}
          onChange={(e) => onAnswer(e.target.value, false)}
          placeholder="Your comments..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          rows="3"
        ></textarea>
      ) : (
        <div className="space-y-3">
          {question.options.map((option) => (
            <label
              key={option}
              className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {question.type === 'multi' ? (
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={() => onAnswer(toggleOption(value, option), false)}
                  className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
              ) : (
                <input
                  type="radio"
                  name={id}
                  checked={value === option}
                  onChange={() => onAnswer(option, true)}
                  className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                />
              )}
              <span className="ml-3 text-sm font-medium text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// A simple error popup modal component
function ErrorPopup({ errorMessage, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Dark overlay behind the error message */}
      <div className="absolute inset-0 bg-black opacity-50" />

      {/* Error dialog */}
      <div className="relative bg-white rounded-lg p-6 z-[10000] shadow-xl max-w-sm w-full">
        <h3 className="text-xl font-semibold mb-4 text-red-600">Error</h3>
        <p className="mb-4 text-gray-700">{errorMessage}</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          OK
        </button>
      </div>
    </div>
  );
}
